import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { isAboveAndOnShape } from '../../_pure/contractHelpers/itemHelpers.js';
import { deriveOpacityPrecedence, resolveEntityItemOpacity } from '../../_pure/itemOpacity.js';
import { resolveConstellationPlacement, } from '../_pure/constellationPlacement.js';
import { resolveEffectiveEntityShape } from '../_pure/effectiveEntityShape.js';
import { geometrySpecSpacingDiameter, resolveItemGeometrySpec, } from '../_pure/geometrySpec.js';
import { resolveItemLabelTransform, resolveItemRenderPlan } from '../_pure/itemRenderPlan.js';
import { resolveVisibleLabelChannels } from '../_pure/labelChannelVisibility.js';
import { resolveLabelRenderPlan } from '../_pure/labelRenderPlan.js';
import { resolveLabel } from '../_pure/labelResolution.js';
import { areRecordReadsUnchanged } from '../_pure/recordReadEquality.js';
import { computeSectorRingLabelGeometryByItemKey } from '../_pure/sectorRingLabelGeometry.js';
import { isVisibleByPresentation } from '../_pure/spaceRendererHelpers.js';
import { createRecordReadLog, trackRecordReads, } from '../recordReadTracking.js';
import { useGpuResources } from '../useGpuResources.js';
import { EntityItemRenderer } from './EntityItemRenderer.js';
import { useVisualizationDiagnostics } from '../../runtime/diagnostics.js';
const DEFAULT_ITEM_COLOR = 'black';
/**
 * Mesh handlers are cached per item and read the latest context through a ref, so they stay
 * identity-stable across renders and never go stale when a memoized item skips re-rendering.
 */
function useStableItemMeshProps(constellationKey, context) {
    const contextRef = useRef(context);
    contextRef.current = context;
    const cacheRef = useRef(new Map());
    const cacheConstellationKeyRef = useRef(constellationKey);
    if (cacheConstellationKeyRef.current !== constellationKey) {
        cacheConstellationKeyRef.current = constellationKey;
        cacheRef.current.clear();
    }
    return (itemKey) => {
        const cached = cacheRef.current.get(itemKey);
        if (cached)
            return cached;
        const meshProps = {
            onClick: (event) => {
                event.stopPropagation();
                contextRef.current.emitItemIntent({
                    itemInteractionIntentKind: 'item-clicked',
                    constellationKey,
                    itemKey,
                });
            },
            onPointerOver: (event) => {
                event.stopPropagation();
                contextRef.current.setHoveredEntity({ constellationKey, itemKey });
            },
            onPointerOut: (event) => {
                event.stopPropagation();
                contextRef.current.setHoveredEntity((current) => {
                    if (current?.constellationKey === constellationKey && current.itemKey === itemKey) {
                        return null;
                    }
                    return current;
                });
            },
        };
        cacheRef.current.set(itemKey, meshProps);
        return meshProps;
    };
}
/**
 * The single layout renderer. It turns a pure placement plan plus pure geometry/material
 * specs into scene nodes; adding a layout kind never requires a new renderer or component.
 */
function renderConstellation(props, getItemMeshProps, ringGeometry) {
    const { constellation, context } = props;
    const constellationKey = constellation.constellationKey;
    const constellationState = context.entityConstellationParametricStateByKey[constellationKey];
    const layout = constellationState?.layout;
    if (!layout)
        return null;
    if (constellationState?.visibility?.visible === false)
        return null;
    if (constellationState?.visibilityRule && !isVisibleByPresentation({
        visibility: constellationState.visibilityRule,
        hoveredEntity: context.hoveredEntity,
        effectiveActiveItemKeyByConstellationKey: context.effectiveActiveItemKeyByConstellationKey,
        effectiveLinkedActiveItemKeysByConstellationKey: context.effectiveLinkedActiveItemKeysByConstellationKey,
    })) {
        return null;
    }
    const items = context.entityItems;
    const itemCount = items.length;
    if (itemCount === 0)
        return null;
    const anchorContext = {
        entityConstellations: context.entityConstellations,
        entityItemsByCollectionKey: context.entityItemsByCollectionKey,
        itemParametricStateByKey: context.itemParametricStateByKey,
        entityConstellationParametricStateByKey: context.entityConstellationParametricStateByKey,
    };
    const itemParametricStateByKey = new Map(items.map((item) => [
        item.itemKey,
        context.itemParametricStateByKey[item.itemKey],
    ]));
    const shapeByItemKey = new Map(items.map((item) => [
        item.itemKey,
        resolveEffectiveEntityShape({
            layout,
            itemParametricState: itemParametricStateByKey.get(item.itemKey),
            itemCount,
        }),
    ]));
    const geometrySpecByItemKey = new Map(items.map((item, index) => [
        item.itemKey,
        resolveItemGeometrySpec({
            layout,
            shape: shapeByItemKey.get(item.itemKey),
            itemIndex: index,
            itemCount,
        }),
    ]));
    const firstGeometrySpec = geometrySpecByItemKey.get(items[0].itemKey);
    const placement = resolveConstellationPlacement({
        constellation,
        constellationState,
        layout,
        items,
        shapeByItemKey,
        layoutNormalOffsetByItemKey: new Map(items.map((item) => [
            item.itemKey,
            itemParametricStateByKey.get(item.itemKey)?.layoutNormalOffset,
        ])),
        spokeSpacingFallback: geometrySpecSpacingDiameter(firstGeometrySpec),
        context: anchorContext,
    });
    if (!placement)
        return null;
    const activeItemKey = context.effectiveActiveItemKeyByConstellationKey[constellationKey];
    const linkedActiveItemKeys = context.effectiveLinkedActiveItemKeysByConstellationKey[constellationKey];
    const dimNonActiveOpacity = context.dimNonActiveOpacityByConstellationKey[constellationKey];
    const hoveredItemKey = context.hoveredEntity?.constellationKey === constellationKey
        ? context.hoveredEntity.itemKey
        : null;
    const labelChannelPolicy = constellationState?.labelChannelPolicy;
    const labelProximity = context.spaceRenderOptions?.labelProximity;
    const sectorRingLabelGeometryByItemKey = layout.layoutKind === 'sector-ring'
        ? computeSectorRingLabelGeometryByItemKey({
            layout,
            items,
            labelLayoutPolicy: constellationState?.labelLayoutPolicy,
        })
        : null;
    const itemByKey = new Map(items.map((item) => [item.itemKey, item]));
    const ringDiagnostics = layout.layoutKind === 'concentric-ring' && ringGeometry
        ? [] : undefined;
    const renderItem = (itemPlacement) => {
        const item = itemByKey.get(itemPlacement.itemKey);
        if (!item)
            return null;
        const itemParametricState = itemParametricStateByKey.get(item.itemKey);
        const explicitPosition = itemParametricState?.position;
        const renderPlacement = explicitPosition === undefined
            ? itemPlacement
            : {
                ...itemPlacement,
                position: resolveConstellationLocalPosition({
                    worldPosition: explicitPosition,
                    framePosition: placement.framePosition,
                    frameRotation: placement.frameRotation,
                    itemFrameRotation: placement.itemFrameRotation,
                }),
                labelAnchorWorldPosition: explicitPosition,
            };
        const shape = shapeByItemKey.get(item.itemKey);
        const precedence = deriveOpacityPrecedence({
            itemParametricState,
            baseItemOpacity: resolveEntityItemOpacity(itemParametricState),
            activeItemKey,
            currentItemKey: item.itemKey,
            linkedActiveItemKeys,
            dimNonActiveOpacity,
        });
        const plan = resolveItemRenderPlan({
            shape, layout, placement: renderPlacement, geometry: geometrySpecByItemKey.get(item.itemKey),
            itemParametricState, precedence, defaultColor: DEFAULT_ITEM_COLOR, dimNonActiveOpacity,
        });
        if (!plan)
            return null;
        const acquire = (surface) => ({
            key: surface.key, geometry: context.resourceCache.getGeometry(surface.geometry),
            material: context.resourceCache.getMaterial(surface.material),
            ...(surface.renderOrder === undefined ? {} : { renderOrder: surface.renderOrder }),
        });
        const surfaces = plan.surfaces.map(acquire);
        const outlines = plan.outlines.map(acquire);
        if (ringDiagnostics && layout.layoutKind === 'concentric-ring') {
            const geometry = geometrySpecByItemKey.get(item.itemKey);
            ringDiagnostics.push({
                itemKey: item.itemKey,
                crossSection: layout.ringTube.crossSectionsByRing?.[itemPlacement.index] ?? 'tube (default)',
                ringRadius: layout.ringRadii?.[itemPlacement.index] ?? layout.startRadius + itemPlacement.index * layout.ringGap,
                tubeRadius: layout.ringTube.tubeRadii?.[itemPlacement.index] ?? layout.ringTube.tubeRadius,
                bandThicknessRatio: layout.ringTube.bandThicknessRatio ?? 0.15,
                bandWidth: layout.ringTube.bandWidth,
                bandThickness: layout.ringTube.bandThickness,
                geometryKind: geometry.geometryKind,
                gpuGeometryTypes: surfaces.map((surface) => surface.geometry.type),
                ...(geometry.geometryKind === 'revolved-profile' ? {
                    radialWidth: Math.max(...geometry.profile.map(([r]) => r)) - Math.min(...geometry.profile.map(([r]) => r)),
                    verticalHeight: Math.max(...geometry.profile.map(([, y]) => y)) - Math.min(...geometry.profile.map(([, y]) => y)),
                } : geometry.geometryKind === 'toothed-band' ? {
                    radialWidth: geometry.outerRadius - geometry.innerRadius,
                    verticalHeight: geometry.halfHeight * 2,
                    toothAxis: geometry.toothAxis,
                    toothCount: geometry.toothCount,
                    toothDepth: geometry.toothDepth,
                } : {}),
            });
        }
        const sectorRingLabelGeometry = sectorRingLabelGeometryByItemKey?.get(item.itemKey);
        // Layout-locked shapes are not carried by the item, so the label host must be told about them.
        const labelItemParametricState = shape === undefined
            ? (itemParametricState ?? {})
            : { ...(itemParametricState ?? {}), shape };
        const visibleLabelChannels = plan.labels ? resolveVisibleLabelChannels({
            item,
            itemParametricState: labelItemParametricState,
            shape,
            labelChannelPolicy,
            activeItemKey,
            hoveredItemKey,
        }) : [];
        const isHovered = hoveredItemKey === item.itemKey;
        const transformResolver = resolveItemLabelTransform(shape);
        const resolvedLabels = visibleLabelChannels.flatMap((channel) => {
            const declaration = labelItemParametricState.label?.[channel];
            if (declaration === undefined)
                return [];
            const resolved = resolveLabel({
                item,
                channel,
                declaration,
                shape,
                itemOpacity: precedence.resolvedOpacity,
                anchorContext,
                labelAnchorWorldPosition: itemPlacement.labelAnchorWorldPosition,
                ...(channel === 'onItem' && sectorRingLabelGeometry
                    ? { layoutOverride: sectorRingLabelGeometry }
                    : {}),
                ...(itemPlacement.labelFrameRotation
                    ? { labelFrameRotation: itemPlacement.labelFrameRotation }
                    : {}),
                ...(layout.layoutKind === 'sector-ring' && itemPlacement.labelFrameRotation
                    ? { labelParentRotation: itemPlacement.labelFrameRotation }
                    : {}),
                ...(transformResolver ? { transformResolver } : {}),
                ...(isHovered && labelProximity?.hoverFontScale !== undefined
                    ? { hoverFontScale: labelProximity.hoverFontScale }
                    : {}),
            });
            return resolved === null ? [] : [resolveLabelRenderPlan(resolved)];
        });
        const meshProps = plan.interaction === 'item' ? getItemMeshProps(item.itemKey) : {};
        return (_jsx(EntityItemRenderer, { plan: plan, surfaces: surfaces, outlines: outlines, resourceCache: context.resourceCache, meshProps: meshProps, labels: resolvedLabels, ...(labelProximity === undefined || isHovered
                ? {}
                : { labelProximity }) }, item.itemKey));
    };
    const guide = placement.guide;
    const guideGeometry = guide
        ? context.resourceCache.getPolylineGeometry(guide.cacheKey, guide.points)
        : null;
    const guideMaterial = guide ? context.resourceCache.getMaterial(guide.material) : null;
    const renderedItems = placement.itemPlacements.map(renderItem);
    return (_jsxs("group", { position: placement.framePosition, rotation: placement.frameRotation, children: [ringDiagnostics ? _jsx(RingGeometryDiagnostic, { snapshot: JSON.stringify({
                    constellationKey, receivedCrossSections: layout.layoutKind === 'concentric-ring'
                        ? layout.ringTube.crossSectionsByRing ?? null : null,
                    items: ringDiagnostics,
                }) }) : null, _jsxs("group", { rotation: placement.itemFrameRotation, children: [guide && guideGeometry && guideMaterial ? (_jsx(GuideLine, { cache: context.resourceCache, geometry: guideGeometry, material: guideMaterial, closed: guide.closed })) : null, renderedItems] })] }));
}
function resolveConstellationLocalPosition(args) {
    if (!Array.isArray(args.worldPosition) || args.worldPosition.length !== 3 || !args.worldPosition.every(Number.isFinite)) {
        throw new Error('Entity item has an invalid world-space position.');
    }
    const frameQuaternion = new THREE.Quaternion().setFromEuler(new THREE.Euler(...args.frameRotation));
    const itemFrameQuaternion = new THREE.Quaternion().setFromEuler(new THREE.Euler(...args.itemFrameRotation));
    const localPosition = new THREE.Vector3(...args.worldPosition)
        .sub(new THREE.Vector3(...args.framePosition))
        .applyQuaternion(frameQuaternion.clone().multiply(itemFrameQuaternion).invert());
    return [localPosition.x, localPosition.y, localPosition.z];
}
function RingGeometryDiagnostic({ snapshot }) {
    useEffect(() => {
        console.info(`[ring-geometry diagnostics] ${snapshot}`);
    }, [snapshot]);
    return null;
}
function GuideLine(props) {
    useGpuResources(props.cache, [props.geometry, props.material]);
    return props.closed
        ? _jsx("lineLoop", { geometry: props.geometry, material: props.material, dispose: null })
        : _jsx("lineSegments", { geometry: props.geometry, material: props.material, dispose: null });
}
function canReuseConstellationRender(cached, next) {
    const previousContext = cached.props.context;
    const nextContext = next.context;
    const constellationKey = next.constellation.constellationKey;
    return cached.props.constellation === next.constellation
        && previousContext.entityItems === nextContext.entityItems
        && previousContext.entityConstellations === nextContext.entityConstellations
        && previousContext.entityItemsByCollectionKey === nextContext.entityItemsByCollectionKey
        && previousContext.spaceRenderOptions === nextContext.spaceRenderOptions
        && previousContext.resourceCache === nextContext.resourceCache
        && previousContext.hoveredEntity === nextContext.hoveredEntity
        && previousContext.setHoveredEntity === nextContext.setHoveredEntity
        && previousContext.emitItemIntent === nextContext.emitItemIntent
        && previousContext.effectiveActiveItemKeyByConstellationKey === nextContext.effectiveActiveItemKeyByConstellationKey
        && previousContext.effectiveLinkedActiveItemKeysByConstellationKey === nextContext.effectiveLinkedActiveItemKeysByConstellationKey
        && Object.is(previousContext.dimNonActiveOpacityByConstellationKey[constellationKey], nextContext.dimNonActiveOpacityByConstellationKey[constellationKey])
        && areRecordReadsUnchanged(cached.itemStateReads, previousContext.itemParametricStateByKey, nextContext.itemParametricStateByKey)
        && areRecordReadsUnchanged(cached.constellationStateReads, previousContext.entityConstellationParametricStateByKey, nextContext.entityConstellationParametricStateByKey);
}
/**
 * Placement can anchor to other constellations, so a constellation is re-rendered whenever any
 * parametric state it actually read during its last render changed identity. During playback
 * only a few constellations change per frame, and the rest reuse their previous element.
 */
export function EntityConstellationRenderer(props) {
    const { ringGeometry } = useVisualizationDiagnostics();
    const getItemMeshProps = useStableItemMeshProps(props.constellation.constellationKey, props.context);
    const cacheRef = useRef(null);
    const cached = cacheRef.current;
    if (cached && cached.ringGeometry === ringGeometry && canReuseConstellationRender(cached, props))
        return cached.element;
    const itemStateReads = createRecordReadLog();
    const constellationStateReads = createRecordReadLog();
    const trackedContext = {
        ...props.context,
        itemParametricStateByKey: trackRecordReads(props.context.itemParametricStateByKey, itemStateReads),
        entityConstellationParametricStateByKey: trackRecordReads(props.context.entityConstellationParametricStateByKey, constellationStateReads),
    };
    // Keep tracking proxies out of React props: dev tooling enumerates props and would
    // turn selective reads into whole-record dependencies.
    const element = renderConstellation({ constellation: props.constellation, context: trackedContext }, getItemMeshProps, ringGeometry);
    cacheRef.current = { props, element, itemStateReads, constellationStateReads, ringGeometry };
    return element;
}
